import { useTranslation } from "react-i18next";
import { Outlet } from "react-router-dom";
import { Col, Flex, Row } from "antd";
import { createStyles } from "antd-style";

import { Typography } from "@av/components/atoms/typography/Typography";
import { LoggedoutLanguageSelector } from "@av/components/molecules/LoggedoutLanguageSelector";

import type { TFunction } from "i18next";

function getGreeting(t: TFunction): string {
  const hour = new Date().getHours();

  if (hour < 12) {
    return t("auth.login.common_morningGreeting");
  } else if (hour < 17) {
    return t("auth.login.common_afternoonGreeting");
  }

  return t("auth.login.common_eveningGreeting");
}

const useStyles = createStyles(({ token, css }) => ({
  container: css`
    min-height: 100vh;
    width: 100vw;
  `,
  row: css`
    width: 100%;
    min-height: 100vh;
    margin: 0;
  `,
  leftPanel: css`
    background: ${token.colorPrimary};
    background-image: repeating-linear-gradient(
      -45deg,
      rgba(255, 255, 255, 0.08),
      rgba(255, 255, 255, 0.08) 2px,
      transparent 2px,
      transparent 40px
    );
    background-size: 60px 60px;
    min-height: 100%;
  `,
  leftContent: css`
    color: ${token.colorTextLightSolid};
    .ant-typography {
      color: inherit;
    }
  `,
  wavingHand: css`
    margin-left: ${token.marginXS}px;
    height: 27px;
  `,
  rightPanel: css`
    @media (max-width: ${token.screenMD}px) {
      padding: ${token.paddingLG}px ${token.paddingMD}px;
    }
  `,
  languageSelector: css`
    position: absolute;
    top: 20px;
    right: 20px;
    @media (max-width: ${token.screenMD}px) {
      top: 15px;
      right: 15px;
    }
  `,
}));

export function LoginLayout() {
  const { styles } = useStyles();
  const { t } = useTranslation();

  return (
    <Flex className={styles.container}>
      <Row className={styles.row}>
        <Col xs={0} md={12}>
          <Flex vertical justify="center" align="center" className={styles.leftPanel}>
            <Flex vertical align="flex-start" className={styles.leftContent}>
              <Typography.Title testIdPrefix="left-greeting" level={4}>
                {getGreeting(t)}
                <img src="/wavingHand.gif" alt="waving hand" className={styles.wavingHand} />
              </Typography.Title>

              <Typography.Title testIdPrefix="lets-get-started" level={1}>
                {t("auth.login.common_letsGetStarted")}
              </Typography.Title>

              <Typography.Text testIdPrefix="left-greeting-text">
                {t("auth.login.common_welcomeMessage")}
              </Typography.Text>
            </Flex>
          </Flex>
        </Col>

        <Col xs={24} md={12} className={styles.rightPanel}>
          <Flex vertical align="center" style={{ height: "100%" }}>
            <div data-testid="language-selector" className={styles.languageSelector}>
              <LoggedoutLanguageSelector />
            </div>

            <Outlet />

            <div />
          </Flex>
        </Col>
      </Row>
    </Flex>
  );
}
